import pLimit from 'p-limit';
import { match, constant, isType } from 'variant';
import pWaitFor, { TimeoutError } from 'p-wait-for';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import type { AccountAuth, DeviceCodeResponse } from '../schemas.js';
import {
  client,
  RenewTokenData,
  DeviceCodeNotYetVerifiedError,
} from '../client.js';
import {
  isValidToken,
  deleteAuthData,
  readAccountAuthData,
  saveAuthData,
} from '../auth-data.js';
import type { AuthStrategyState, AuthStrategy } from './auth-strategy-types.js';
import { apiKeyAuthStrategy } from './api-key-auth-strategy.js';

const seconds = (n: number) => 1000 * n;

async function finalizeLogin(tokenData: Omit<AccountAuth, 'userInfo'>) {
  const userInfo = await client.userInfo({
    accessToken: tokenData.accessToken,
  });

  const result = { ...tokenData, userInfo };

  await deleteAuthData();
  await saveAuthData(result);

  return result;
}

export async function loginWithDeviceCode(
  { deviceCode, expiresIn }: DeviceCodeResponse,
  { signal }: { signal: AbortSignal }
) {
  try {
    const authData = await pWaitFor(
      async () => {
        try {
          const tokenData = await client.createToken({
            deviceCode,
          });

          return pWaitFor.resolveWith(tokenData);
        } catch (error) {
          if (error instanceof DeviceCodeNotYetVerifiedError) {
            // continue polling
            return false;
          }

          throw error;
        }
      },
      {
        interval: getTestOverrides().loginWithDeviceCodeInterval ?? seconds(3),
        timeout: { milliseconds: seconds(expiresIn), signal },
      }
    );

    return await finalizeLogin(authData);
  } catch (error) {
    if (error instanceof TimeoutError) {
      throw new CliError({
        code: CliErrorCode.DeviceCodeTimedOut(),
        cause: error,
      });
    }

    throw error;
  }
}

export async function loginWithRefreshToken({
  refreshToken,
}: {
  refreshToken: string;
}) {
  const renewTokenResult = await client.renewToken({ refreshToken });

  if (isType(renewTokenResult, RenewTokenData.TokenRenewed)) {
    return await finalizeLogin(renewTokenResult.payload);
  }

  throw new CliError({
    code: CliErrorCode.FailedToLoginWithRefreshToken(),
    cause: null,
  });
}

export async function renewAccountAuth({
  refreshToken,
  userInfo,
}: AccountAuth) {
  const renewTokenResult = await client.renewToken({ refreshToken });

  if (isType(renewTokenResult, RenewTokenData.TokenRenewed)) {
    await saveAuthData({ ...renewTokenResult.payload, userInfo });
  }

  return renewTokenResult;
}

export async function getNonExpiredAccountAuth(): Promise<AccountAuth | null> {
  const accountAuthStatus = await readAccountAuthData();

  return match(accountAuthStatus, {
    Authenticated: (accountAuth) => accountAuth,
    Unauthenticated: () => null,
    Expired: async (accountAuth) => {
      const renewTokenResult = await renewAccountAuth(accountAuth);

      return match(renewTokenResult, {
        TokenRenewed: ({ payload: newTokenData }) => ({
          ...newTokenData,
          userInfo: accountAuth.userInfo,
        }),
        RefreshTokenRevoked: constant(null),
      });
    },
  });
}

function createAccountAuthState({
  userInfo,
  ...initialTokenData
}: AccountAuth): AuthStrategyState {
  const limiter = pLimit(1);
  let tokenData = { ...initialTokenData };

  return {
    getAccessToken: () => {
      return limiter(async () => {
        if (isValidToken(tokenData)) {
          return tokenData.accessToken;
        }

        const renewTokenResult = await renewAccountAuth({
          ...tokenData,
          userInfo,
        });

        return match(renewTokenResult, {
          TokenRenewed: ({ payload: newTokenData }) => {
            tokenData = newTokenData;
            return tokenData.accessToken;
          },
          RefreshTokenRevoked: ({ cause }) => {
            throw new CliError({
              code: CliErrorCode.FailedToRenewAuthTokenRefreshTokenRevoked(),
              cause,
            });
          },
        });
      });
    },
    getRefreshToken: () => tokenData.refreshToken,
    userInfo,
  };
}

export const accountAuthStrategy: AuthStrategy = {
  async isLoggedIn() {
    const apiKeyAuth = await apiKeyAuthStrategy.isLoggedIn();

    if (apiKeyAuth) {
      return apiKeyAuth;
    }

    const accountAuth = await getNonExpiredAccountAuth();

    if (accountAuth) {
      return createAccountAuthState(accountAuth);
    }

    return null;
  },
  async login({ deviceCode, signal }) {
    const accountAuth = await loginWithDeviceCode(deviceCode, { signal });

    return createAccountAuthState(accountAuth);
  },
};
